import {
  CoinsIcon,
  CogIcon,
  DrillIcon,
  FlaskConicalIcon,
  FuelIcon,
  GlobeIcon,
  GraduationCapIcon,
  ShieldCheckIcon,
  TruckIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  context: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Oil & Gas Industry Fundamentals: The Foundation',
    subtitle: 'The foundation your students need to understand everything else',
    context:
      'Your first introduction to Oil & Gas can be transformative or overwhelming. This volume transforms technical concepts into something comprehensible and relevant from day one.',
    items: [
      'Hydrocarbons and petroleum fundamentals',
      'Crude oil and natural gas',
      'Origin and formation of hydrocarbons',
      'Basic crude oil characteristics',
      'Natural gas fundamentals',
      'Oil & Gas industry structure',
      'Upstream, Midstream & Downstream',
      'Complete Oil & Gas value chain',
      'Exploration-to-consumer journey',
      'Petroleum products and their uses',
      'Oil & Gas infrastructure',
      'Industry participants',
      'Global importance of Oil & Gas',
      'Essential industry terminology',
      'Connections between industry segments',
    ],
    result:
      'Your students understand Oil & Gas as a practical discipline that connects geology, engineering, global markets, and energy transition — not an impossible list of terms to memorize.',
    icon: FuelIcon,
  },
  {
    number: '2',
    title: 'Petroleum Geology, Reservoirs & Exploration',
    subtitle: 'Everything your students need to know about geological foundations and petroleum systems',
    context:
      'This is where many students struggle to build the scientific foundation. This volume gives you the materials to help them truly understand petroleum geology.',
    items: [
      'Petroleum geology fundamentals',
      'Geological time and sedimentary basins',
      'Organic matter and hydrocarbon formation',
      'Source rocks',
      'Reservoir rocks',
      'Seals and traps',
      'Hydrocarbon migration',
      'Petroleum systems',
      'Porosity and permeability',
      'Oil and gas reservoirs',
      'Reservoir fluids',
      'Reservoir pressure',
      'Geological structures',
      'Petroleum exploration',
      'Seismic exploration concepts',
      'Exploration wells',
      'Discovery and appraisal',
      'Resources and reserves',
      'Geological uncertainty',
      'Exploration risk and commercial considerations',
      '15 practical petroleum geology case studies',
    ],
    result: 'Your students learn the geological principles that explain where oil and gas are found and how they form.',
    icon: GlobeIcon,
  },
  {
    number: '3',
    title: 'Drilling & Well Construction',
    subtitle: 'How to understand Oil & Gas wells and the drilling process',
    context:
      'Drilling is complex but essential to understand. This volume gives you everything needed so your students grasp well construction clearly.',
    items: [
      'Purpose of drilling',
      'Oil & Gas wells',
      'Drilling rigs',
      'Wellbores',
      'Drill bits and drill strings',
      'Drilling fluids',
      'Casing',
      'Cementing',
      'Well integrity',
      'Formation pressure concepts',
      'Vertical wells',
      'Directional wells',
      'Horizontal wells',
      'Onshore drilling',
      'Offshore drilling',
      'Well trajectories',
      'Well construction stages',
      'Common drilling challenges',
      'Relationship between drilling and production',
      '15 worked drilling and well construction examples',
    ],
    result:
      'Your students understand how Oil & Gas wells are created and the engineering involved in accessing underground resources.',
    icon: DrillIcon,
  },
  {
    number: '4',
    title: 'Oil & Gas Production Operations',
    subtitle: 'How Oil & Gas moves from the reservoir to the surface',
    context:
      'Production is where exploration investment becomes value. This volume makes this critical stage clear and comprehensible.',
    items: [
      'Production fundamentals',
      'Reservoir pressure and fluid movement',
      'Production wells',
      'Natural flow',
      'Artificial lift concepts',
      'Primary recovery',
      'Secondary recovery',
      'Enhanced recovery concepts',
      'Oil, gas, and water production',
      'Surface production facilities',
      'Separation',
      'Produced water',
      'Associated and non-associated gas',
      'Gathering systems and flowlines',
      'Field processing',
      'Production monitoring concepts',
      'Production decline',
      'Mature fields',
      'Field life cycle',
      'Production system relationships',
      '15 practical production operation case studies',
    ],
    result: 'Your students understand how Oil & Gas is extracted from underground and processed at the wellhead.',
    icon: CogIcon,
  },
  {
    number: '5',
    title: 'Oil Refining, Natural Gas Processing & Petroleum Products',
    subtitle: 'How crude oil and natural gas are transformed into useful products',
    context: 'Refining and processing complete the value chain. This volume makes this essential step clear.',
    items: [
      'Crude oil characteristics',
      'Purpose of refineries',
      'Atmospheric distillation',
      'Vacuum distillation',
      'Separation processes',
      'Conversion and cracking concepts',
      'Treatment and blending',
      'Gasoline / Petrol',
      'Diesel',
      'Jet fuel',
      'LPG',
      'Fuel oil',
      'Lubricants',
      'Petrochemical feedstocks',
      'Petroleum-derived products',
      'Natural gas composition',
      'Raw natural gas',
      'Natural gas processing',
      'Impurity removal concepts',
      'Natural Gas Liquids',
      'Pipeline-quality gas',
      'LNG fundamentals',
      'Connections between processing, products, and consumers',
      '15 practical refining and processing case studies',
    ],
    result:
      'Your students understand how crude oil and natural gas become the fuels and products used worldwide.',
    icon: FlaskConicalIcon,
  },
  {
    number: '6',
    title: 'Midstream Operations, Pipelines, Storage & Transportation',
    subtitle: 'The infrastructure connecting production with global consumers',
    context: 'Midstream is the backbone of global Oil & Gas supply. This volume explains it clearly.',
    items: [
      'Midstream fundamentals',
      'Gathering systems',
      'Crude oil pipelines',
      'Natural gas pipelines',
      'Pumping concepts',
      'Compression concepts',
      'Compressor stations',
      'Oil and gas storage',
      'Tank farms',
      'Underground gas storage',
      'Terminals',
      'Oil tankers',
      'Product tankers',
      'LNG carriers',
      'Liquefaction and regasification',
      'LNG terminals',
      'Rail transportation',
      'Road transportation',
      'Transportation capacity',
      'Infrastructure bottlenecks',
      'Supply-chain reliability',
      'Global Oil & Gas logistics',
      '12 practical pipeline and transportation case studies',
    ],
    result: 'Your students understand how Oil & Gas moves through global supply chains to reach consumers.',
    icon: TruckIcon,
  },
  {
    number: '7',
    title: 'Oil & Gas Safety, Risk & Environmental Management',
    subtitle: 'Teaching safety and environmental concepts responsibly',
    context: 'Safety and environment are critical. This volume helps you teach them clearly and responsibly.',
    items: [
      'Hazard vs. risk',
      'Occupational safety',
      'Process safety',
      'Hydrocarbon hazards',
      'Pressurized systems',
      'Safety barriers',
      'Human factors',
      'Safety culture',
      'Near misses and incidents',
      'Incident learning',
      'Risk management concepts',
      'Loss of containment',
      'Environmental impacts',
      'Oil spills',
      'Methane emissions',
      'Flaring and venting',
      'Produced water',
      'Waste management',
      'Soil and water impacts',
      'Marine impacts',
      'Biodiversity',
      'Environmental monitoring',
      'Mitigation',
      'Site restoration',
      'Decommissioning',
      '15 practical safety and environmental case studies',
    ],
    result:
      'Your students understand that Oil & Gas safety and environmental management are built on evidence and continuous improvement.',
    icon: ShieldCheckIcon,
  },
  {
    number: '8',
    title: 'Oil & Gas Economics, Global Markets & Energy Transition',
    subtitle: 'Connecting technical Oil & Gas knowledge with economics and future energy systems',
    context:
      'This is the big-picture volume. Your students learn how Oil & Gas fits into global energy systems and economic realities.',
    items: [
      'Oil & Gas project economics',
      'Exploration and development costs',
      'Production and transportation costs',
      'Commercial viability',
      'Supply and demand',
      'Inventories',
      'Production capacity',
      'Price volatility',
      'International oil trade',
      'Natural gas markets',
      'LNG markets',
      'Imports and exports',
      'Global supply chains',
      'Supply disruptions',
      'Energy security',
      'Infrastructure and market interdependence',
      'Energy transition',
      'Renewable energy',
      'Electrification',
      'Energy efficiency',
      'Changing Oil & Gas demand',
      'Methane reduction',
      'Carbon management',
      'Carbon capture concepts',
      'Hydrogen',
      'Energy access',
      'Affordability and reliability',
      'Regional differences',
      'Future energy scenarios',
      '15 real-world economics and energy transition case studies',
    ],
    result: 'Your students understand Oil & Gas economics and its evolving role in global energy systems.',
    icon: CoinsIcon,
  },
  {
    number: '9',
    title: 'Teaching the Oil & Gas Industry: Classroom Excellence',
    subtitle: 'Your complete guide as an Oil & Gas education professional',
    context:
      'This volume gives you exactly what you need to plan, organize, and evaluate your entire year of Oil & Gas teaching.',
    items: [
      'How to introduce the Oil & Gas industry',
      'Organizing technical concepts for students',
      'Teaching industry terminology',
      'Explaining complex concepts simply',
      'Connecting Upstream, Midstream & Downstream',
      'Teaching through cause and effect',
      'Classroom discussion strategies',
      'Critical-thinking approaches',
      'Connecting theory with real-world contexts',
      'Addressing common misconceptions',
      'Teaching different student levels',
      'Teaching safety and environmental topics responsibly',
      'Teaching economics and global markets',
      'Teaching energy transition',
      'Connecting the complete Oil & Gas value chain',
      'Classroom planning and instructional strategies',
      '10 complete integrated Oil & Gas projects',
    ],
    result:
      'You\'ll never wonder "what do I teach today?" again. Everything is planned and contextualized for maximum student learning.',
    icon: GraduationCapIcon,
  },
];
